import { useQuery, useQueryClient } from "@tanstack/react-query"
import BaseModal from "../../../../components/layout/BaseModal"
import ErrorPage from "../../../404/ErrorPage"
import dayjs from "dayjs"
import { useMemo, useState } from "react"
import Loading from "../../../../components/common/Loading"
import { AssignPayReqElement } from "./AssignPayReqElement"
import { formatCurrency, useDelayWhenSearchWithPagination } from "../../../../services/functions"
import { getPaginatedData } from "../../../api/apiCalls"
import { defaultTransactionStatus } from "../../../../data/defaultValues"
import { GeneralQueryKeys } from "../../../../api/constants"
import BaseSearchInputField from "../../../../components/elements/BaseSearchInputField"
import BaseGrowBody from "../../../../components/elements/BaseGrowBody"
import { Pagination } from "antd"

import { transactionElementType } from "../../../Buchhaltung/types"
import { useUpdateTransactionMutation } from "../../../Buchhaltung/api/services"
import { P } from "../../../../auth/permissions"
import { Authorized } from "../../../../components/authentication/Authorized"
type AssignTransactionModalType = {
    modalId: number
    transactionQueryKey: string
    transaction?: transactionElementType
    closeModal: () => void
}
export default function AssignTransactionModal(props: AssignTransactionModalType) {
    const [page, setpage] = useState(1)
    const handlePageChange = (page: number) => {
        setpage(page)
    }
    const [searchTerm, setsearchTerm] = useState("")
    const searchParams = useDelayWhenSearchWithPagination(searchTerm)
    const queryKey = GeneralQueryKeys.PAYREQS_ALL + searchParams + page
    const { data, error, isLoading } = useQuery({
        queryKey: [queryKey],
        queryFn: () =>
            getPaginatedData({
                pageType: "paymentrequirements",
                page: page,
                limit: 15,
                search: searchParams,
                status: "unpaid",
            }),
    })
    const displayedPayReq = useMemo(() => data?.docs, [data?.docs])

    // ASSIGN TRANSACTION TO PAYREQ
    const [showAll, setshowAll] = useState<boolean>(false)
    const queryClient = useQueryClient()
    const { mutate: assignTransaction } = useUpdateTransactionMutation(queryClient, queryKey, props.transactionQueryKey)
    const handleAssignTransactionToPayreq = (pid: string) => {
        setshowAll(false)
        assignTransaction({
            pid: pid,
            tid: props?.transaction?._id ?? "",
        })
        props.closeModal()
    }

    return (
        <Authorized allOf={[P.buchhaltung_zahlungen_read_write]}>
            <BaseModal title={"Zahlungsanforderung zuordnen"} modalId={props.modalId}>
                <div className="flex flex-col h-full overflow-hidden">
                    <div className="border-b-2 flex flex-row px-6 pb-2 pt-3 gap-4">
                        <div className="flex flex-col w-48 items-end">
                            <p className="underline text-xs text-gray-400">Betrag:</p>
                            <p
                                className={`font-bold text-xl ${
                                    props.transaction?.processingStatus === defaultTransactionStatus.UNASSIGNED
                                        ? "text-red-600"
                                        : props.transaction?.processingStatus === defaultTransactionStatus.ASSIGNED
                                        ? "text-green-500"
                                        : "text-black"
                                }`}>
                                {formatCurrency(props.transaction?.paymentInformation?.transactionAmount?.amount?.valueOf() ?? 0)}
                            </p>
                            <p className="underline text-xs text-gray-400 pt-1">Datum:</p>
                            <p className="font-semibold text-gray-500">{dayjs(props.transaction?.bookingTimestamp).format("DD.MM.YYYY")}</p>
                        </div>
                        <div className="flex flex-col items-start w-fit overflow-y-scroll">
                            <p className="underline text-xs text-gray-400">Sender: </p>
                            {props.transaction?.paymentInformation.counterpartNames.map((item, index) => (
                                <p className="text-xs text-gray-400 w-fit" title={item} key={index}>
                                    {item}
                                </p>
                            ))}
                        </div>
                        <div className="flex flex-col items-start w-full">
                            <p className="underline text-xs text-gray-400">Verwendungszweck: </p>
                            <p className="line-clamp-2 text-xs text-gray-400" title={props.transaction?.paymentInformation.remittanceText}>
                                {props.transaction?.paymentInformation.remittanceText}
                            </p>
                        </div>
                    </div>
                    {/* Bottom Row */}
                    <div className="flex flex-row justify-between py-2">
                        <BaseSearchInputField small placeholder="Nach Rechnungsnummer suchen" searchFunction={setsearchTerm} value={searchTerm} />
                    </div>
                    <BaseGrowBody classProps="overflow-y-scroll">
                        {error && <ErrorPage />}
                        {isLoading && <Loading />}
                        {data && !isLoading && !error && (
                            <>
                                {displayedPayReq.length === 0 && <p className="text-center text-gray-400 text-sm">Keine Zahlungsanforderungen gefunden</p>}
                                {displayedPayReq.map(
                                    (
                                        item: any,
                                        index: number // TODO type ändern
                                    ) => (
                                        <AssignPayReqElement
                                            func={() => handleAssignTransactionToPayreq(item._id)}
                                            key={index}
                                            due_date={dayjs(item?.due_date).format("DD.MM.YYYY")}
                                            invoice_date={dayjs(item.invoice_date).format("DD.MM.YYYY")}
                                            nr={item?.bill_id?.nr}
                                            amount={item.expected_payment}
                                            customer={item?.customer_id?.customer?.name}
                                            administrator={item?.customer_id?.administrator?.name}
                                            status={item.status === "settled"}
                                        />
                                    )
                                )}
                            </>
                        )}
                    </BaseGrowBody>
                    <div className="flex justify-center w-full p-4">
                        <Pagination
                            defaultCurrent={1}
                            current={page}
                            pageSize={15}
                            onChange={handlePageChange}
                            total={data?.totalDocs}
                            showSizeChanger={false}
                        />
                    </div>
                </div>
            </BaseModal>
        </Authorized>
    )
}
